import React from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

const parseDate = (value) => {
  if (!value) return null;

  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCenterDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) return value || '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const formatSideDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) return value || '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
  }).format(date);
};

const formatMenuDateLabel = (value) => {
  const date = parseDate(value);
  if (!date) return value || '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
};

const getDateYear = (value) => {
  const date = parseDate(value);
  if (!date) return null;

  return date.getFullYear();
};

export default function CalendarDateNav({
  selectedDate,
  previousDate,
  nextDate,
  previousDates = [],
  nextDates = [],
  onDateChange,
}) {
  const [previousMenuAnchor, setPreviousMenuAnchor] = React.useState(null);
  const [nextMenuAnchor, setNextMenuAnchor] = React.useState(null);

  const hasPreviousDates = Array.isArray(previousDates) && previousDates.length > 0;
  const hasNextDates = Array.isArray(nextDates) && nextDates.length > 0;

  const closeMenus = () => {
    setPreviousMenuAnchor(null);
    setNextMenuAnchor(null);
  };

  const handleDateClick = (date) => {
    closeMenus();
    if (date) {
      onDateChange?.(date);
    }
  };

  if (!selectedDate) {
    return null;
  }

  const previousMenuDates = [...previousDates].reverse();
  const nextMenuDates = [...nextDates];

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: {
          xs: '44px 1fr 1.3fr 1fr 44px',
          md: '44px 1fr 1.4fr 1fr 44px',
        },
        alignItems: 'stretch',
        borderTop: '1px solid #d7d7d7',
        borderBottom: '1px solid #d7d7d7',
        bgcolor: '#fff',
        mb: 2,
      }}
    >
      <IconButton
        size="small"
        disabled={!hasPreviousDates}
        onClick={(event) => setPreviousMenuAnchor(event.currentTarget)}
        sx={{
          borderRadius: 0,
          borderRight: '1px solid #d7d7d7',
          color: '#374365',
        }}
      >
        <KeyboardDoubleArrowLeftIcon fontSize="small" />
      </IconButton>

      <DateCell
        disabled={!previousDate}
        label={previousDate ? formatSideDateLabel(previousDate) : ''}
        onClick={() => handleDateClick(previousDate)}
        sx={{
          fontSize: { xs: 11, md: 12 },
          borderRight: '1px solid #d7d7d7',
        }}
      />

      <DateCell
        selected
        label={formatCenterDateLabel(selectedDate)}
        sx={{
          fontSize: { xs: 13, md: 16 },
          fontWeight: 700,
          color: '#0b4f1f',
          borderRight: '1px solid #d7d7d7',
        }}
      />

      <DateCell
        disabled={!nextDate}
        label={nextDate ? formatSideDateLabel(nextDate) : ''}
        onClick={() => handleDateClick(nextDate)}
        sx={{
          fontSize: { xs: 11, md: 12 },
          borderRight: '1px solid #d7d7d7',
        }}
      />

      <IconButton
        size="small"
        disabled={!hasNextDates}
        onClick={(event) => setNextMenuAnchor(event.currentTarget)}
        sx={{
          borderRadius: 0,
          color: '#374365',
        }}
      >
        <KeyboardDoubleArrowRightIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={previousMenuAnchor}
        open={Boolean(previousMenuAnchor)}
        onClose={closeMenus}
        PaperProps={{
          sx: {
            minWidth: 220,
            maxHeight: 360,
          },
        }}
      >
        <DateMenuItems
          dates={previousMenuDates}
          onDateClick={handleDateClick}
        />
      </Menu>

      <Menu
        anchorEl={nextMenuAnchor}
        open={Boolean(nextMenuAnchor)}
        onClose={closeMenus}
        PaperProps={{
          sx: {
            minWidth: 220,
            maxHeight: 360,
          },
        }}
      >
        <DateMenuItems
          dates={nextMenuDates}
          onDateClick={handleDateClick}
        />
      </Menu>
    </Box>
  );
}

function DateMenuItems({ dates = [], onDateClick }) {
  let currentYear = null;

  return dates.map((date) => {
    const year = getDateYear(date);
    const shouldShowYearDivider = year != null && year !== currentYear;

    if (shouldShowYearDivider) {
      currentYear = year;
    }

    return (
      <React.Fragment key={date}>
        {shouldShowYearDivider ? (
          <Box
            sx={{
              px: 1.5,
              pt: 0.75,
              pb: 0.5,
              pointerEvents: 'none',
            }}
          >
            <Divider>
              <Chip
                size="small"
                variant="outlined"
                label={year}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#374365',
                  bgcolor: '#fff',
                }}
              />
            </Divider>
          </Box>
        ) : null}

        <MenuItem
          onClick={() => onDateClick?.(date)}
          sx={{
            minHeight: 36,
            fontSize: 14,
          }}
        >
          {formatMenuDateLabel(date)}
        </MenuItem>
      </React.Fragment>
    );
  });
}

function DateCell({
  label,
  selected = false,
  disabled = false,
  onClick,
  sx = {},
}) {
  return (
    <ButtonBase
      disabled={disabled || selected}
      onClick={onClick}
      sx={{
        minHeight: 48,
        px: { xs: 0.5, md: 1 },
        width: '100%',
        bgcolor: selected ? '#fff' : '#fafafa',
        cursor: disabled || selected ? 'default' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        '&:hover': {
          bgcolor: disabled || selected ? undefined : '#f1f1f1',
        },
        ...sx,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}